import { useCallback, useEffect, useMemo, useRef } from 'react';

import { setCurrentPosition, setCollisionData } from '../use-positioning/actions';
import { CollisionDetectionService } from '../../services/collision-detection-service';
import { DropdownService } from '../../services/dropdown-service';
import { PositionService } from '../../services/position-service';
import { usePositioningReducer } from '../use-positioning/reducer';
import { useMaxZ } from '../services/use-max-z';
import { useWindowBoundingBox } from '../use-window-bounding-box';

import { getNextPosition } from './get-next-position';

const validPositions = DropdownService.getValidPositions();

export const usePositioning = (ref, startingPosition) => {
  /** @type {React.RefObject<HTMLElement>} */
  const dropdownRef = useRef();
  const [state, dispatch] = usePositioningReducer(startingPosition);
  const { currentPosition, collisionData } = state;
  const windowBoundingBox = useWindowBoundingBox();
  const reset = useCallback(() => {
    dispatch(setCurrentPosition(startingPosition));
    dispatch(setCollisionData([]));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reset();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [windowBoundingBox]);

  useEffect(() => {
    const dropdownBoundingBox = dropdownRef.current.getBoundingClientRect();
    const allCollisions = collisionData.concat(CollisionDetectionService.getCollisions(dropdownBoundingBox, windowBoundingBox));
    const nextPosition = getNextPosition(allCollisions, currentPosition);

    if (nextPosition !== currentPosition) {
      dispatch(setCollisionData(allCollisions));
      dispatch(setCurrentPosition(nextPosition));
    }
  });

  const zIndex = useMaxZ();

  let left = 0;
  let top = 0;

  if (ref.current) {
    ({ left, top } = PositionService.getStyleFromBoundingBox(currentPosition, ref.current.getBoundingClientRect(), validPositions));
  }

  const style = useMemo(
    () => ({
      left,
      top,
      zIndex,
    }),
    [left, top, zIndex],
  );

  return {
    position: currentPosition,
    ref: dropdownRef,
    style,
    windowBoundingBox,
  };
};
